/** How multiple feature names are combined in a single feature check. */
export const FeatureMatchMode = {
  /** All listed feature names must be present (logical AND). */
  All: 'all',
  /** At least one listed feature name must be present (logical OR). */
  Some: 'some',
} as const;

export type FeatureMatchModeValue =
  (typeof FeatureMatchMode)[keyof typeof FeatureMatchMode];

/**
 * How the per-channel results are combined for an account-wide check (i.e. when no `channelId` is
 * given). Independent of `FeatureMatchMode`, which combines multiple feature names.
 */
export const ChannelMatchMode = {
  /** The feature must be present on EVERY channel (intersection across channels). */
  All: 'all',
  /** The feature counts as present when booked on at least one channel (union across channels). */
  Some: 'some',
} as const;

export type ChannelMatchModeValue =
  (typeof ChannelMatchMode)[keyof typeof ChannelMatchMode];

export type HasFeatureParams = {
  /** One feature name or several to check. */
  featureNames: string | readonly string[];
  /**
   * How to combine multiple feature names.
   * @default FeatureMatchMode.All
   */
  mode?: FeatureMatchModeValue;
  /**
   * Optional channel to scope the channel-level lookup to a single channel. With `channelId`,
   * `hasFeature()` searches that channel's features first (then falls back to `accountLevel`);
   * without it, the channels are combined per `channelMatch` (default `ChannelMatchMode.All` —
   * present on every channel) before the same `accountLevel` fallback.
   */
  channelId?: string;
  /**
   * For an account-wide check (ignored when `channelId` is set), how to combine the channels:
   * `ChannelMatchMode.All` (default) → present on EVERY channel (intersection);
   * `ChannelMatchMode.Some` → present on any channel (union). The `accountLevel` fallback applies
   * regardless.
   * @default ChannelMatchMode.All
   */
  channelMatch?: ChannelMatchModeValue;
};
